import { useEffect, useRef, useState } from "react";

import {
  CALL_CONTROL_IDS,
  CONTROL_REASONS,
  DEFAULT_LABELS,
  isControlEnabled,
  type ControlId,
} from "../lib/controls";
import { useControls } from "../lib/useControls";
import { useStore } from "../lib/store";
import { ShortcutBadge } from "./CommandBar";
import { cx } from "./ui";

function enabledTone(id: ControlId): string {
  if (id === "hangup") return "border-fail/40 bg-fail/5 text-fail hover:bg-red-500/10";
  if (id === "otp_sent") return "border-teal/25 bg-teal/5 text-live hover:bg-live/10";
  if (id === "accept") return "border-teal/25 bg-teal/5 text-teal hover:bg-teal/10";
  return "border-teal/25 bg-teal/5 text-ink hover:bg-white/5";
}

function isLiveCall(state: string | null | undefined): boolean {
  const value = (state ?? "").toUpperCase();
  return Boolean(value) && value !== "IDLE" && value !== "DONE";
}

function readCsrf(): string {
  const names = ["csrf_token", "csrf", "X-CSRF-Token", "csrftoken"];
  for (const name of names) {
    const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1");
    const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
    if (match?.[1]) return decodeURIComponent(match[1]);
  }
  return "";
}

/** Inline QC POST — credentials + CSRF cookie. Not ControlId / not api.ts. */
async function postQc(path: string): Promise<number> {
  const csrf = readCsrf();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (csrf) headers["X-CSRF-Token"] = csrf;
  const response = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers,
  });
  return response.status;
}

type AudioContextCtor = {
  new (contextOptions?: AudioContextOptions): AudioContext;
};

function audioContextCtor(): AudioContextCtor | undefined {
  const w = window as Window & { webkitAudioContext?: AudioContextCtor };
  return window.AudioContext || w.webkitAudioContext;
}

function resampleLinear(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate === toRate || input.length === 0) return input;
  const ratio = toRate / fromRate;
  const outLen = Math.max(1, Math.round(input.length * ratio));
  const out = new Float32Array(outLen);
  const last = input.length - 1;
  for (let i = 0; i < outLen; i++) {
    const src = i / ratio;
    const i0 = Math.min(last, Math.floor(src));
    const i1 = Math.min(last, i0 + 1);
    const frac = src - i0;
    out[i] = input[i0] * (1 - frac) + input[i1] * frac;
  }
  return out;
}

function tailSlice(pcm: ArrayBuffer, channels: number, rate: number, ms: number): ArrayBuffer {
  const frame = Math.max(1, channels) * 2;
  const keep = Math.max(frame, Math.floor((rate * ms) / 1000) * frame);
  if (pcm.byteLength <= keep * 2) return pcm;
  const start = pcm.byteLength - keep;
  const aligned = start - (start % frame);
  return pcm.slice(aligned);
}

function enqueuePcm(
  ctx: AudioContext,
  pcm: ArrayBuffer,
  channels: number,
  rate: number,
  sched: { next: number },
): void {
  const ch = Math.max(1, channels);
  const frameBytes = ch * 2;
  const usable = pcm.byteLength - (pcm.byteLength % frameBytes);
  if (usable < frameBytes || ctx.state === "closed") return;
  const samples = new Int16Array(pcm.slice(0, usable));
  const frames = Math.floor(samples.length / ch);
  if (frames < 1) return;
  const floats: Float32Array[] = [];
  for (let c = 0; c < ch; c++) {
    const mono = new Float32Array(frames);
    for (let i = 0; i < frames; i++) {
      mono[i] = samples[i * ch + c] / 32768;
    }
    floats.push(ctx.sampleRate === rate ? mono : resampleLinear(mono, rate, ctx.sampleRate));
  }
  const outFrames = floats[0].length;
  const buffer = ctx.createBuffer(ch, outFrames, ctx.sampleRate);
  for (let c = 0; c < ch; c++) {
    buffer.copyToChannel(floats[c], c);
  }
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  src.connect(ctx.destination);
  const now = ctx.currentTime;
  if (sched.next < now + 0.02) sched.next = now + 0.02;
  src.start(sched.next);
  sched.next += buffer.duration;
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const timer = window.setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        window.clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });
}

type ToastFn = (message: string, level?: "info" | "warn" | "error" | "ok") => void;

async function pumpTapPcm(opts: {
  ctx: AudioContext;
  signal: AbortSignal;
  toast: ToastFn;
}): Promise<void> {
  const { ctx, signal, toast } = opts;
  let after = 0;
  let primed = false;
  let waitingSince = Date.now();
  let toasted404 = false;
  const sched = { next: 0 };

  while (!signal.aborted) {
    let response: Response;
    try {
      response = await fetch(`/api/session/tap-pcm?after=${after}`, {
        credentials: "include",
        signal,
        headers: { Accept: "application/octet-stream" },
      });
    } catch {
      if (signal.aborted) return;
      await sleep(200, signal);
      continue;
    }
    if (signal.aborted) return;

    if (response.status === 404) {
      if (!toasted404 && Date.now() - waitingSince >= 4000) {
        toasted404 = true;
        toast("waiting for recording", "info");
      }
      await sleep(200, signal);
      continue;
    }

    if (response.status === 204) {
      waitingSince = Date.now();
      const hdr = Number(response.headers.get("X-Tap-After"));
      if (Number.isFinite(hdr) && hdr >= 0) after = hdr;
      await sleep(200, signal);
      continue;
    }

    if (!response.ok) {
      await sleep(200, signal);
      continue;
    }

    waitingSince = Date.now();
    const rate = Number(response.headers.get("X-Tap-Rate") || 8000) || 8000;
    const channels = Number(response.headers.get("X-Tap-Channels") || 2) || 2;
    const nextAfter = Number(response.headers.get("X-Tap-After"));
    let body: ArrayBuffer;
    try {
      body = await response.arrayBuffer();
    } catch {
      if (signal.aborted) return;
      await sleep(200, signal);
      continue;
    }
    if (Number.isFinite(nextAfter) && nextAfter >= 0) {
      after = nextAfter;
    } else {
      after += body.byteLength;
    }
    if (body.byteLength < 2) {
      await sleep(150, signal);
      continue;
    }
    const play = primed ? body : tailSlice(body, channels, rate, 400);
    primed = true;
    try {
      if (ctx.state === "suspended") await ctx.resume();
      enqueuePcm(ctx, play, channels, rate, sched);
    } catch {
      /* keep polling */
    }
  }
}

/**
 * Primary call-action buttons. Enablement is exactly `ui.actions` (parked / SEND OTP NOW / live hangup).
 * Redial | Listen on one row. Listen is Web Audio of live mix PCM. No barge. No second command strip.
 */
export function ActionBar() {
  const { state, operator, toast, openNewCall } = useStore();
  const { config, showBadges } = useControls();
  const actions = state.snapshot.actions;
  const compact = config.density === "compact";
  const live = isLiveCall(state.snapshot.state);
  const [listening, setListening] = useState(false);
  const [qcHint, setQcHint] = useState<string | undefined>();
  const listeningRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  listeningRef.current = listening;

  const closeCtx = () => {
    const ctx = ctxRef.current;
    ctxRef.current = null;
    if (ctx && ctx.state !== "closed") {
      void ctx.close().catch(() => undefined);
    }
  };

  const stopListen = async () => {
    abortRef.current?.abort();
    abortRef.current = null;
    closeCtx();
    listeningRef.current = false;
    setListening(false);
    try {
      await postQc("/api/session/listen-stop");
    } catch {
      /* UI already stopped */
    }
  };

  useEffect(() => {
    if (!live && listeningRef.current) void stopListen();
  }, [live]);

  const run: Record<(typeof CALL_CONTROL_IDS)[number], () => void> = {
    accept: () => void operator.accept(),
    retry: () => void operator.retry(),
    resend: () => void operator.resend(),
    otp_sent: () => void operator.otpSent(),
    hangup: () => void operator.hangup(),
  };

  const runListen = async () => {
    if (!live) return;
    if (listeningRef.current) {
      await stopListen();
      return;
    }
    try {
      const status = await postQc("/api/session/listen");
      if (status === 404 || status === 501) {
        setQcHint("not yet");
        toast("not yet", "info");
        return;
      }
      if (status >= 200 && status < 300) {
        const Ctor = audioContextCtor();
        if (!Ctor) {
          toast("Listen failed", "warn");
          return;
        }
        const ctx = new Ctor({ sampleRate: 8000 });
        ctxRef.current = ctx;
        if (ctx.state === "suspended") {
          try {
            await ctx.resume();
          } catch {
            /* autoplay — click already happened */
          }
        }
        const ac = new AbortController();
        abortRef.current = ac;
        listeningRef.current = true;
        setListening(true);
        setQcHint(undefined);
        void pumpTapPcm({ ctx, signal: ac.signal, toast });
        return;
      }
      toast("Listen failed", "warn");
    } catch {
      setQcHint("not yet");
      toast("not yet", "info");
    }
  };

  const canRedial = Boolean(actions.redial);
  const canNewCall = !live && actions.new_call !== false;
  const qcTitle = qcHint ?? (live ? "hear live mix in browser" : "no live call");
  const qcBtn = cx(
    "inline-flex items-center justify-center gap-2 rounded-xl border font-medium tracking-wide transition",
    compact ? "min-h-[44px] px-3 text-[13px]" : "min-h-[52px] px-4 text-[14px]",
  );

  return (
    <div className="flex flex-col gap-2 px-3 md:px-4">
      <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
        {CALL_CONTROL_IDS.map((id) => {
          const enabled = isControlEnabled(id, actions);
          const label = config.labels[id].trim() || DEFAULT_LABELS[id];
          return (
            <button
              key={id}
              type="button"
              disabled={!enabled}
              title={enabled ? label : CONTROL_REASONS[id]}
              aria-keyshortcuts={showBadges && config.shortcuts[id] ? config.shortcuts[id] : undefined}
              onClick={run[id]}
              className={cx(
                "inline-flex items-center justify-center gap-2 rounded-xl border font-medium tracking-wide transition",
                compact ? "min-h-[52px] px-3 text-[13px]" : "min-h-[72px] px-4 text-[15px]",
                enabled ? enabledTone(id) : "cursor-not-allowed border-white/5 bg-transparent text-mute/50",
              )}
            >
              <span>{label}</span>
              {showBadges ? <ShortcutBadge value={config.shortcuts[id]} /> : null}
            </button>
          );
        })}
      </div>
      <div className="flex flex-col gap-2 md:max-w-md">
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            disabled={!canNewCall}
            title={canNewCall ? "New call" : "Call in progress"}
            onClick={() => openNewCall(true)}
            className={cx(
              qcBtn,
              canNewCall
                ? "border-teal/40 bg-teal/10 text-teal hover:bg-teal/15"
                : "cursor-not-allowed border-white/5 bg-transparent text-mute/50",
            )}
          >
            New call
          </button>
          <button
            type="button"
            disabled={!canRedial}
            title={canRedial ? "Redial" : "Nothing to redial"}
            onClick={() => void operator.redial()}
            className={cx(
              qcBtn,
              canRedial
                ? "border-border bg-transparent text-ink-2 hover:bg-white/5 hover:text-ink"
                : "cursor-not-allowed border-white/5 bg-transparent text-mute/50",
            )}
          >
            Redial
          </button>
          <button
            type="button"
            disabled={!live}
            title={qcTitle}
            aria-pressed={listening}
            onClick={() => void runListen()}
            className={cx(
              qcBtn,
              live
                ? listening
                  ? "border-accent/40 bg-accent/10 text-accent hover:bg-accent/15"
                  : "border-border bg-transparent text-ink-2 hover:bg-white/5 hover:text-ink"
                : "cursor-not-allowed border-white/5 bg-transparent text-mute/50",
            )}
          >
            {listening ? "Stop" : "Listen"}
          </button>
        </div>
      </div>
    </div>
  );
}
